import { format } from 'date-fns';
import { ArrowDownLeft, ArrowUpRight, Mail, Eye, MousePointerClick } from 'lucide-react';
import { cn } from '@/lib/utils';
import { MessageStatus } from './MessageStatus';
import { MediaPreview } from './MediaPreview';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type { ThreadMessage } from '@/types/omniChannel';

interface MessageBubbleProps {
  message: ThreadMessage;
}

export function MessageBubble({ message }: MessageBubbleProps) {
  const isInbound = message.direction === 'inbound';
  const isEmail = message.type === 'email';

  return (
    <div
      className={cn(
        'flex flex-col max-w-[70%] mb-3',
        isInbound ? 'items-start' : 'items-end ml-auto'
      )}
    >
      {/* Sender label for inbound messages */}
      {isInbound && (
        <div className="flex items-center gap-2 mb-1 px-1">
          <span className="text-[11px] text-muted-foreground">
            {message.sender_name || message.from_number || 'Customer'}
          </span>
          {isEmail && (
            <Badge variant="primary-soft" className="px-1.5 py-0">
              <Mail className="h-2.5 w-2.5" />
            </Badge>
          )}
        </div>
      )}

      {/* Sender label for outbound messages */}
      {!isInbound && (
        <div className="flex items-center gap-2 mb-1 px-1">
          <span className="text-[11px] text-muted-foreground">
            {message.sender_name || 'You'}
          </span>
          {isEmail && (
            <Badge variant="primary-soft" className="px-1.5 py-0">
              <Mail className="h-2.5 w-2.5" />
            </Badge>
          )}
        </div>
      )}

      {/* Email subject line */}
      {isEmail && message.subject && (
        <p className={cn(
          "text-xs font-medium mb-1 px-1 truncate max-w-full",
          isInbound ? "text-foreground" : "text-muted-foreground"
        )}>
          {message.subject}
        </p>
      )}

      {/* Message bubble */}
      <div
        className={cn(
          'px-4 py-2.5 rounded-[14px]',
          isInbound
            ? 'rounded-bl-[4px] border border-border bg-card text-foreground'
            : 'rounded-br-[4px] bg-primary text-primary-foreground'
        )}
      >
        <p className="text-[13px] whitespace-pre-wrap break-words">
          {message.content}
        </p>

        {/* Media attachments */}
        {message.media_urls && message.media_urls.length > 0 && (
          <MediaPreview
            mediaUrls={message.media_urls}
            className={cn(
              isInbound ? '' : 'border-primary-foreground/20'
            )}
          />
        )}
      </div>

      {/* Metadata row */}
      <div
        className={cn(
          'flex items-center gap-1.5 mt-1 px-1',
          isInbound ? 'flex-row' : 'flex-row-reverse'
        )}
      >
        {/* Direction arrow */}
        {isInbound ? (
          <ArrowDownLeft className="h-3 w-3 text-muted-foreground" />
        ) : (
          <ArrowUpRight className="h-3 w-3 text-primary" />
        )}

        {/* Timestamp */}
        <span className="text-[11px] text-muted-foreground">
          {format(new Date(message.timestamp), 'h:mm a')}
        </span>

        {/* Message status for outbound */}
        {!isInbound && message.status && (
          <MessageStatus
            status={message.status}
            openedCount={message.opened_count}
            clickedCount={message.clicked_count}
            bounceReason={message.bounce_reason}
          />
        )}

        {/* Engagement badges for email */}
        {!isInbound && isEmail && (message.opened_count || message.clicked_count) && (
          <TooltipProvider>
            <div className="flex items-center gap-1 ml-1">
              {message.opened_count && message.opened_count > 0 && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="flex items-center gap-0.5 text-[10px] text-info">
                      <Eye className="h-3 w-3" />
                      {message.opened_count > 1 && message.opened_count}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="text-xs">
                    Opened {message.opened_count} time{message.opened_count > 1 ? 's' : ''}
                  </TooltipContent>
                </Tooltip>
              )}
              {message.clicked_count && message.clicked_count > 0 && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="flex items-center gap-0.5 text-[10px] text-primary">
                      <MousePointerClick className="h-3 w-3" />
                      {message.clicked_count > 1 && message.clicked_count}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="text-xs">
                    Clicked {message.clicked_count} link{message.clicked_count > 1 ? 's' : ''}
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          </TooltipProvider>
        )}
      </div>
    </div>
  );
}
